import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  AuthTokens,
  BaseOperationResult
} from '../types';
import { API_CONFIG } from '../config/api';
import { AuthenticatedApiService } from './authenticatedApiService';
import { usersService } from './usersService';
import { tenantService } from './tenantService';
import { subscriptionService } from './subscriptionService';

export interface ReportData {
  id: string;
  name: string;
  description: string;
  type: 'subscription' | 'user' | 'tenant' | 'role' | 'activity';
  data: any[];
  generatedAt: string;
  filters?: {
    dateRange?: {
      startDate: string;
      endDate: string;
    };
    tenantId?: string;
    subscriptionId?: string;
    roleId?: string;
  };
}

export interface SubscriptionReport {
  totalSubscriptions: number;
  activeSubscriptions: number;
  inactiveSubscriptions: number;
  expiredSubscriptions: number;
  subscriptionsByTenant: Array<{
    tenantName: string;
    count: number;
  }>;
  subscriptionsByStatus: Array<{
    status: string;
    count: number;
  }>;
  subscriptionsByMonth: Array<{
    month: string;
    count: number;
  }>;
}

export interface UserActivityReport {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  newUsersThisMonth: number;
  usersByTenant: Array<{
    tenantName: string;
    count: number;
  }>;
  usersByRole: Array<{
    roleName: string;
    count: number;
  }>;
  userActivityByMonth: Array<{
    month: string;
    activeUsers: number;
    newUsers: number;
  }>;
}

export interface TenantUsageReport {
  totalTenants: number;
  activeTenants: number;
  inactiveTenants: number;
  tenantsByStatus: Array<{
    status: string;
    count: number;
  }>;
  tenantUsageStats: Array<{
    tenantName: string;
    userCount: number;
    subscriptionCount: number;
    lastActivity: string;
  }>;
}

class ReportingApiService extends AuthenticatedApiService {
  /**
   * Get subscription report data by aggregating subscription data
   */
  async getSubscriptionReport(filters?: any): Promise<BaseOperationResult<SubscriptionReport>> {
    try {
      console.log('📊 Building subscription report from available data...');
      
      // Fetch all subscriptions
      const subscriptionsResponse = await subscriptionService.getSubscriptions({ page: 1, pageSize: 1000 });
      if (!subscriptionsResponse.succeeded) {
        throw new Error('Failed to fetch subscriptions data');
      }
      const subscriptions = subscriptionsResponse.data?.items || subscriptionsResponse.data || [];
      console.log('📊 Subscriptions data for report:', subscriptions.length, 'subscriptions found');

      // Fetch tenants data to map tenant IDs to names
      const tenantsResponse = await tenantService.getTenants({ page: 1, pageSize: 1000 });
      if (!tenantsResponse.succeeded) {
        throw new Error('Failed to fetch tenants data');
      }
      const tenants = tenantsResponse.data?.items || tenantsResponse.data || [];
      const tenantMap = new Map(tenants.map((t: any) => [t.id, t.name]));

      // Calculate totals
      const totalSubscriptions = subscriptions.length;
      const activeSubscriptions = subscriptions.filter((s: any) => s.status === 'Active').length;
      const inactiveSubscriptions = subscriptions.filter((s: any) => s.status === 'Inactive').length;
      const expiredSubscriptions = subscriptions.filter((s: any) => s.status === 'Expired').length;

      // Subscriptions by tenant
      const subscriptionsByTenantMap = new Map<string, number>();
      subscriptions.forEach((s: any) => {
        const tenantName = tenantMap.get(s.tenantId) || 'Unknown Tenant';
        subscriptionsByTenantMap.set(tenantName, (subscriptionsByTenantMap.get(tenantName) || 0) + 1);
      });
      const subscriptionsByTenant = Array.from(subscriptionsByTenantMap, ([tenantName, count]) => ({ tenantName, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Subscriptions by status
      const subscriptionsByStatusMap = new Map<string, number>();
      subscriptions.forEach((s: any) => {
        const status = s.status || 'Unknown';
        subscriptionsByStatusMap.set(status, (subscriptionsByStatusMap.get(status) || 0) + 1);
      });
      const subscriptionsByStatus = Array.from(subscriptionsByStatusMap, ([status, count]) => ({ status, count }));

      // Subscriptions by month
      const monthlySubscriptions = new Map<string, number>();
      subscriptions.forEach((s: any) => {
        const createdAt = new Date(s.createdAt || s.created_at || Date.now());
        const month = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
        monthlySubscriptions.set(month, (monthlySubscriptions.get(month) || 0) + 1);
      });
      const subscriptionsByMonth = Array.from(monthlySubscriptions, ([month, count]) => ({ month, count }))
        .sort((a, b) => a.month.localeCompare(b.month));

      const report: SubscriptionReport = {
        totalSubscriptions,
        activeSubscriptions,
        inactiveSubscriptions,
        expiredSubscriptions,
        subscriptionsByTenant,
        subscriptionsByStatus,
        subscriptionsByMonth,
      };

      return {
        succeeded: true,
        data: report,
        errorDescription: null,
        errorCode: 0
      };
    } catch (error) {
      console.error('Get subscription report error:', error);
      return {
        succeeded: false,
        data: null as any,
        errorDescription: error instanceof Error ? error.message : 'Unknown error',
        errorCode: 500
      };
    }
  }

  /**
   * Get user activity report data by aggregating user data
   */
  async getUserActivityReport(filters?: any): Promise<BaseOperationResult<UserActivityReport>> {
    try {
      console.log('📊 Building user activity report from available data...');
      
      // Get users data to build the report
      const usersResponse = await usersService.getUsers({ page: 1, pageSize: 1000 });
      
      if (!usersResponse.succeeded) {
        throw new Error('Failed to fetch users data');
      }

      const users = usersResponse.data?.items || usersResponse.data || [];
      console.log('📊 Users data for report:', users.length, 'users found');

      // Calculate report data
      const totalUsers = users.length;
      const activeUsers = users.filter((user: any) => user.isActive !== false).length;
      const inactiveUsers = totalUsers - activeUsers;
      const newUsersThisMonth = users.filter((user: any) => {
        const createdAt = new Date(user.createdAt || user.created_at || Date.now());
        const now = new Date();
        const monthAgo = new Date(now.getFullYear(), now.getMonth(), 1);
        return createdAt >= monthAgo;
      }).length;

      // Get tenants data to map tenant IDs to names
      const tenantsResponse = await tenantService.getTenants({ page: 1, pageSize: 1000 });
      const tenants = tenantsResponse.succeeded ? (tenantsResponse.data?.items || tenantsResponse.data || []) : [];
      
      // Create a mapping of tenant ID to tenant name
      const tenantMap = new Map();
      tenants.forEach((tenant: any) => {
        tenantMap.set(tenant.id, tenant.name);
      });

      // Group users by tenant with proper tenant names
      const usersByTenant = users.reduce((acc: any[], user: any) => {
        let tenantName = 'Unknown Tenant';
        
        // Try to get tenant name from various sources
        if (user.tenantName) {
          tenantName = user.tenantName;
        } else if (user.tenantId && tenantMap.has(user.tenantId)) {
          tenantName = tenantMap.get(user.tenantId);
        } else if (user.organization) {
          tenantName = user.organization;
        }
        
        const existing = acc.find(item => item.tenantName === tenantName);
        if (existing) {
          existing.count++;
        } else {
          acc.push({ tenantName, count: 1 });
        }
        return acc;
      }, []);

      // Group users by role
      const usersByRole = users.reduce((acc: any[], user: any) => {
        const roleName = user.roleName || user.role || 'Unknown Role';
        const existing = acc.find(item => item.roleName === roleName);
        if (existing) {
          existing.count++;
        } else {
          acc.push({ roleName, count: 1 });
        }
        return acc;
      }, []);

      // Generate monthly activity data
      const monthlyActivity = new Map<string, { newUsers: number }>();
      users.forEach((user: any) => {
        const createdAt = new Date(user.createdAt || user.created_at || Date.now());
        const month = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
        const data = monthlyActivity.get(month) || { newUsers: 0 };
        data.newUsers += 1;
        monthlyActivity.set(month, data);
      });

      const sortedMonths = Array.from(monthlyActivity.keys()).sort();

      const userActivityByMonth = sortedMonths.map(month => {
        const newUsers = monthlyActivity.get(month)!.newUsers;
        
        const monthEndDate = new Date(month + '-01');
        monthEndDate.setMonth(monthEndDate.getMonth() + 1);
        monthEndDate.setDate(0);

        const activeUsers = users.filter((user: any) => {
          const createdAt = new Date(user.createdAt || user.created_at || Date.now());
          return user.isActive !== false && createdAt <= monthEndDate;
        }).length;

        return {
          month,
          newUsers,
          activeUsers,
        };
      });

      const report: UserActivityReport = {
        totalUsers,
        activeUsers,
        inactiveUsers,
        newUsersThisMonth,
        usersByTenant,
        usersByRole,
        userActivityByMonth
      };

      return {
        succeeded: true,
        data: report,
        errorDescription: null,
        errorCode: 0
      };
    } catch (error) {
      console.error('Get user activity report error:', error);
      return {
        succeeded: false,
        data: null as any,
        errorDescription: error instanceof Error ? error.message : 'Unknown error',
        errorCode: 500
      };
    }
  }

  /**
   * Get tenant usage report data by aggregating tenant and user data
   */
  async getTenantUsageReport(filters?: any): Promise<BaseOperationResult<TenantUsageReport>> {
    try {
      console.log('📊 Building tenant usage report from available data...');
      
      // Get tenants data
      const tenantsResponse = await tenantService.getTenants({ page: 1, pageSize: 1000 });
      
      if (!tenantsResponse.succeeded) {
        throw new Error('Failed to fetch tenants data');
      }

      const tenants = tenantsResponse.data?.items || tenantsResponse.data || [];
      console.log('📊 Tenants data for report:', tenants.length, 'tenants found');

      // Get users data to calculate usage
      const usersResponse = await usersService.getUsers({ page: 1, pageSize: 1000 });
      const users = usersResponse.succeeded ? (usersResponse.data?.items || usersResponse.data || []) : [];

      // Calculate report data
      const totalTenants = tenants.length;
      const activeTenants = tenants.filter((tenant: any) => tenant.isActive !== false).length;
      const inactiveTenants = totalTenants - activeTenants;

      // Group tenants by status
      const tenantsByStatus = tenants.reduce((acc: any[], tenant: any) => {
        const status = tenant.isActive !== false ? 'Active' : 'Inactive';
        const existing = acc.find(item => item.status === status);
        if (existing) {
          existing.count++;
        } else {
          acc.push({ status, count: 1 });
        }
        return acc;
      }, []);

      // Calculate usage stats per tenant
      const tenantUsageStats = tenants.map((tenant: any) => {
        const tenantUsers = users.filter((user: any) => 
          user.tenantId === tenant.id || user.tenantName === tenant.name
        );
        
        // Placeholder for subscriptionCount and lastActivity, as real data is not yet integrated
        const subscriptionCount = 0; // This should come from actual subscription data
        const lastActivity = ''; // This should come from actual user activity data
        
        return {
          tenantName: tenant.name || tenant.tenantName || 'Unknown Tenant',
          userCount: tenantUsers.length,
          subscriptionCount: subscriptionCount,
          lastActivity: lastActivity
        };
      });

      const report: TenantUsageReport = {
        totalTenants,
        activeTenants,
        inactiveTenants,
        tenantsByStatus,
        tenantUsageStats
      };

      return {
        succeeded: true,
        data: report,
        errorDescription: null,
        errorCode: 0
      };
    } catch (error) {
      console.error('Get tenant usage report error:', error);
      return {
        succeeded: false,
        data: null as any,
        errorDescription: error instanceof Error ? error.message : 'Unknown error',
        errorCode: 500
      };
    }
  }

  /**
   * Export report data
   */
  async exportReport(reportType: string, format: 'csv' | 'pdf' | 'excel', filters?: any): Promise<Blob> {
    try {
      console.log(`📊 Exporting ${reportType} report as ${format}...`);

      let data: any;
      switch (reportType) {
        case 'subscriptions':
          data = await this.getSubscriptionReport(filters);
          break;
        case 'user-activity':
          data = await this.getUserActivityReport(filters);
          break;
        case 'tenant-usage':
          data = await this.getTenantUsageReport(filters);
          break;
        default:
          throw new Error(`Unknown report type: ${reportType}`);
      }

      if (!data.succeeded) {
        throw new Error(`Failed to fetch report data: ${data.errorDescription}`);
      }

      const reportData = data.data;

      // Handle PDF export
      if (format === 'pdf') {
        const doc = new jsPDF();
        const title = `${reportType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Report`;
        doc.text(title, 14, 16);

        if (reportType === 'subscriptions' && reportData) {
          const subReport = reportData as SubscriptionReport;
          autoTable(doc, {
            startY: 22,
            head: [['Metric', 'Value']],
            body: [
              ['Total Subscriptions', subReport.totalSubscriptions],
              ['Active Subscriptions', subReport.activeSubscriptions],
              ['Inactive Subscriptions', subReport.inactiveSubscriptions],
              ['Expired Subscriptions', subReport.expiredSubscriptions],
            ],
          });
          autoTable(doc, {
            head: [['Tenant', 'Count']],
            body: subReport.subscriptionsByTenant.map(item => [item.tenantName, item.count]),
          });
          autoTable(doc, {
            head: [['Status', 'Count']],
            body: subReport.subscriptionsByStatus.map(item => [item.status, item.count]),
          });
          autoTable(doc, {
            head: [['Month', 'Count']],
            body: subReport.subscriptionsByMonth.map(item => [item.month, item.count]),
          });

        } else if (reportType === 'user-activity' && reportData) {
          const userReport = reportData as UserActivityReport;
          autoTable(doc, {
            startY: 22,
            head: [['Metric', 'Value']],
            body: [
              ['Total Users', userReport.totalUsers],
              ['Active Users', userReport.activeUsers],
              ['Inactive Users', userReport.inactiveUsers],
              ['New Users This Month', userReport.newUsersThisMonth],
            ],
          });
          autoTable(doc, {
            head: [['Tenant', 'Count']],
            body: userReport.usersByTenant.map(item => [item.tenantName, item.count]),
          });
          autoTable(doc, {
            head: [['Role', 'Count']],
            body: userReport.usersByRole.map(item => [item.roleName, item.count]),
          });
          autoTable(doc, {
            head: [['Month', 'Active Users', 'New Users']],
            body: userReport.userActivityByMonth.map(item => [item.month, item.activeUsers, item.newUsers]),
          });

        } else if (reportType === 'tenant-usage' && reportData) {
          const tenantReport = reportData as TenantUsageReport;
          autoTable(doc, {
            startY: 22,
            head: [['Metric', 'Value']],
            body: [
              ['Total Tenants', tenantReport.totalTenants],
              ['Active Tenants', tenantReport.activeTenants],
              ['Inactive Tenants', tenantReport.inactiveTenants],
            ],
          });
          autoTable(doc, {
            head: [['Status', 'Count']],
            body: tenantReport.tenantsByStatus.map(item => [item.status, item.count]),
          });
          autoTable(doc, {
            head: [['Tenant', 'User Count', 'Subscription Count', 'Last Activity']],
            body: tenantReport.tenantUsageStats.map(item => [item.tenantName, item.userCount, item.subscriptionCount, item.lastActivity]),
          });
        }

        return doc.output('blob');
      }

      // Handle CSV export
      let csvContent = '';
      if (reportType === 'subscriptions' && reportData) {
        const subReport = reportData as SubscriptionReport;
        csvContent += `Total Subscriptions,${subReport.totalSubscriptions}\n`;
        csvContent += `Active Subscriptions,${subReport.activeSubscriptions}\n`;
        csvContent += `Inactive Subscriptions,${subReport.inactiveSubscriptions}\n`;
        csvContent += `Expired Subscriptions,${subReport.expiredSubscriptions}\n\n`;
        
        csvContent += 'Subscriptions by Tenant\n';
        csvContent += 'Tenant,Count\n';
        subReport.subscriptionsByTenant.forEach(item => {
          csvContent += `"${item.tenantName}",${item.count}\n`;
        });
        csvContent += '\n';

        csvContent += 'Subscriptions by Status\n';
        csvContent += 'Status,Count\n';
        subReport.subscriptionsByStatus.forEach(item => {
          csvContent += `${item.status},${item.count}\n`;
        });
        csvContent += '\n';
        
        csvContent += 'Subscriptions by Month\n';
        csvContent += 'Month,Count\n';
        subReport.subscriptionsByMonth.forEach(item => {
            csvContent += `${item.month},${item.count}\n`;
        });

      } else if (reportType === 'user-activity' && reportData) {
        const userReport = reportData as UserActivityReport;
        csvContent += `Total Users,${userReport.totalUsers}\n`;
        csvContent += `Active Users,${userReport.activeUsers}\n`;
        csvContent += `Inactive Users,${userReport.inactiveUsers}\n`;
        csvContent += `New Users This Month,${userReport.newUsersThisMonth}\n\n`;

        csvContent += 'Users by Tenant\n';
        csvContent += 'Tenant,Count\n';
        userReport.usersByTenant.forEach(item => {
            csvContent += `"${item.tenantName}",${item.count}\n`;
        });
        csvContent += '\n';

        csvContent += 'Users by Role\n';
        csvContent += 'Role,Count\n';
        userReport.usersByRole.forEach(item => {
            csvContent += `"${item.roleName}",${item.count}\n`;
        });
        csvContent += '\n';

        csvContent += 'User Activity by Month\n';
        csvContent += 'Month,Active Users,New Users\n';
        userReport.userActivityByMonth.forEach(item => {
            csvContent += `${item.month},${item.activeUsers},${item.newUsers}\n`;
        });

      } else if (reportType === 'tenant-usage' && reportData) {
        const tenantReport = reportData as TenantUsageReport;
        csvContent += `Total Tenants,${tenantReport.totalTenants}\n`;
        csvContent += `Active Tenants,${tenantReport.activeTenants}\n`;
        csvContent += `Inactive Tenants,${tenantReport.inactiveTenants}\n\n`;

        csvContent += 'Tenants by Status\n';
        csvContent += 'Status,Count\n';
        tenantReport.tenantsByStatus.forEach(item => {
            csvContent += `${item.status},${item.count}\n`;
        });
        csvContent += '\n';

        csvContent += 'Tenant Usage Stats\n';
        csvContent += 'Tenant,User Count,Subscription Count,Last Activity\n';
        tenantReport.tenantUsageStats.forEach(item => {
            csvContent += `"${item.tenantName}",${item.userCount},${item.subscriptionCount},${item.lastActivity}\n`;
        });
      }

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      return blob;
    } catch (error) {
      console.error('Export report error:', error);
      throw error;
    }
  }
}

// Create a singleton instance
const reportingApiService = new ReportingApiService();

// Export the service with token management
export const reportingService = {
  /**
   * Initialize the service with tokens and refresh callback
   */
  initialize(tokens: AuthTokens | null, onTokenRefresh: (tokens: AuthTokens) => void) {
    reportingApiService.setTokens(tokens, onTokenRefresh);
    // Also initialize the other services we depend on
    usersService.initialize(tokens, onTokenRefresh);
    tenantService.initialize(tokens, onTokenRefresh);
    subscriptionService.initialize(tokens, onTokenRefresh);
  },

  /**
   * Get subscription report data
   */
  async getSubscriptionReport(filters?: any): Promise<BaseOperationResult<SubscriptionReport>> {
    return await reportingApiService.getSubscriptionReport(filters);
  },

  /**
   * Get user activity report data
   */
  async getUserActivityReport(filters?: any): Promise<BaseOperationResult<UserActivityReport>> {
    return await reportingApiService.getUserActivityReport(filters);
  },

  /**
   * Get tenant usage report data
   */
  async getTenantUsageReport(filters?: any): Promise<BaseOperationResult<TenantUsageReport>> {
    return await reportingApiService.getTenantUsageReport(filters);
  },

  /**
   * Export report data
   */
  async exportReport(reportType: string, format: 'csv' | 'pdf' | 'excel', filters?: any): Promise<Blob> {
    return await reportingApiService.exportReport(reportType, format, filters);
  },

  /**
   * Check if service is authenticated
   */
  isAuthenticated(): boolean {
    return reportingApiService.isAuthenticated();
  }
};